import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

type DateInput = string | number | Date | null | undefined;

const pad = (n: number) => String(n).padStart(2, "0");

function toDate(dateInput: DateInput): Date | null {
	if (dateInput === null || dateInput === undefined || dateInput === "") return null;
	const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
	return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * The admin's single date format, identical in Arabic and English: DD/MM/YYYY (Gregorian, Latin digits).
 * The second argument is accepted for backwards compatibility and ignored.
 */
export function formatDate(dateInput: DateInput, _locale?: string): string {
	const date = toDate(dateInput);
	if (!date) return "-";
	return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

/** DD/MM/YYYY HH:mm (24-hour), same in both languages. */
export function formatDateTime(dateInput: DateInput, _locale?: string): string {
	const date = toDate(dateInput);
	if (!date) return "-";
	return `${formatDate(date)} ${formatTime(date)}`;
}

/** HH:mm (24-hour). */
export function formatTime(dateInput: DateInput): string {
	const date = toDate(dateInput);
	if (!date) return "-";
	return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function formatCurrency(amount: number, currency = "SAR"): string {
	if (amount === null || amount === undefined) return "0.00";
	const code = currency?.trim() || "SAR";
	try {
		return new Intl.NumberFormat("en-SA", {
			style: "currency",
			currency: code,
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(amount);
	} catch {
		return `${Number(amount).toFixed(2)} ${code}`;
	}
}

export const formatNumber = (num: number) => {
	return new Intl.NumberFormat("en-SA").format(num);
};

export const getBestBadgeVariant = (status: string): any => {
	const statuses = {
		pending: "warning",
		confirmed: "success",
		shipped: "info",
		delivered: "success",
		cancelled: "destructive",
		refunded: "violet",
		paid: "success",
		partial: "warning",
		overdue: "destructive",
		completed: "success",
		approved: "success",
		received: "success",
		rejected: "danger",
		processing: "info",
		ready: "success",
		processed: "success",
		true: "success",
		false: "destructive",
		active: "success",
		inactive: "secondary",
		on_leave: "warning",
		info: "info",
		violet: "violet",
		orange: "orange",
		success: "success",
		warning: "warning",
		danger: "danger",
		outline: "outline",
		secondary: "secondary",
		default: "default",
		destructive: "destructive",
	};

	return statuses[status.toLowerCase() as keyof typeof statuses];
};
